/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-risk-overview-config/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useContext, useState, useEffect } from 'react';
import '@components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';
import { TabsContext } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables';
// eslint-disable-next-line max-len
import { RISK_OVERVIEW_CHECKBOXES } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-risk-overview-config/edit-risk-overview-config.data';
// eslint-disable-next-line max-len
import { formRiskOverviewData } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-risk-overview-config/edit-risk-overview-config.helper';
import {
    EditRiskOverviewConfigProps,
    IRiskOverviewData,
} from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-risk-overview-config/edit-risk-overview-config.interface';
import TabHeading from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/tab-heading';
import { FieldSelector } from '@components/central-monitoring/field-selector';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import ItemWrapper from '@components/core/item-wrapper';
import { useBoolean, useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { DashboardType, FieldTag } from '@types/graphql-global-types';
import { GET_STUDY, SAVE_RISK_OVERVIEW_CONFIGURATION } from '@queries/studies';
import './styles.scss';
import {
    saveRiskOverviewConfigurationVariables,
    saveRiskOverviewConfiguration,
} from '@queries/generated/studies.generated';
import { textToId } from '@utils/automation-test-helpers';

const EditRiskOverviewConfig = ({ study, canEdit, activeTab }: EditRiskOverviewConfigProps) => {
    const { t } = useTranslation();

    const initialState = formRiskOverviewData(study);

    const disabledSaveButton = useBoolean(true);
    const disabledUndoButton = useBoolean(true);

    const [state, setState] = useState<IRiskOverviewData>({ ...initialState });
    const [subjectCalculationError, setSubjectCalculationError] = useState('');

    const [saveRiskOverview] = useMutation<saveRiskOverviewConfiguration, saveRiskOverviewConfigurationVariables>(
        SAVE_RISK_OVERVIEW_CONFIGURATION,
    );
    const tabsContext = useContext(TabsContext);

    useEffect(() => {
        if (study?.riskOverviewConfiguration) {
            setState(formRiskOverviewData(study));
        }
    }, [study]);

    useEffect(() => {
        const stateJson = JSON.stringify(state);
        const initStateJson = JSON.stringify(initialState);

        const hasChanges =
            stateJson !== initStateJson ||
            state.displayRiskOverviewOnStudySummary !== initialState.displayRiskOverviewOnStudySummary;

        disabledUndoButton.setValue(!hasChanges);
        disabledSaveButton.setValue(!hasChanges);
        tabsContext.setChangesContain(hasChanges);
    }, [state]);

    useEffect(() => {
        setSubjectCalculationError('');
        setState({ ...initialState }); // reset unsaved changes after change tab
    }, [activeTab]);

    const onChangeCheckbox = (fieldName: keyof IRiskOverviewData) => () => {
        disabledUndoButton.setFalse();
        disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: !state[fieldName] });
    };

    const onChangeSubjectField = (subjectField: string) => {
        disabledUndoButton.setFalse();
        disabledSaveButton.setFalse();
        setState({ ...state, subjectField });
        setSubjectCalculationError('');
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const onUpdate = () => {
        disabledSaveButton.setTrue();
        const isValidSubjectCalculation = state.numSubjects ? state.subjectField !== '' : true;
        if (!isValidSubjectCalculation) {
            setSubjectCalculationError(t('ra:requiredFieldError'));
            return;
        }
        saveRiskOverview({
            variables: {
                ...state,
                studyId: study.id,
            } as saveRiskOverviewConfigurationVariables,
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest();
            })
            .catch(() => {
                errorRequest();
            });
    };

    const onUndo = () => {
        disabledSaveButton.setTrue();
        disabledUndoButton.setTrue();
        setSubjectCalculationError('');
        setState({ ...initialState });
    };

    const tabActions = [
        {
            element: (
                <DarkTooltip title={t('common:undo')} placement="top" arrow>
                    <FontAwesomeIcon icon={['fal', 'undo']} size="xl" />
                </DarkTooltip>
            ),
            action: onUndo,
            disabled: disabledUndoButton.value || !canEdit,
            'data-testid': 'undo-button',
        },
        {
            element: (
                <DarkTooltip title={t('common:save')} placement="top" arrow>
                    <FontAwesomeIcon icon={['fal', 'floppy-disk']} size="xl" />
                </DarkTooltip>
            ),
            action: onUpdate,
            disabled: disabledSaveButton.value || !canEdit,
            'data-testid': 'save-button',
        },
    ];

    return (
        <div>
            <TabHeading
                id="risk-overview-study-summary-display"
                displayValue={state.displayRiskOverviewOnStudySummary}
                setDisplay={onChangeCheckbox('displayRiskOverviewOnStudySummary')}
                actions={tabActions}
            />
            <ItemWrapper title={t('admin:selectItemsToDisplay')} className="risk-overview-config-wrapper">
                <div className="risk-overview-conf">
                    {RISK_OVERVIEW_CHECKBOXES.map((checkbox, index) => (
                        <p key={checkbox} className="risk-overview-conf__item">
                            <Checkbox
                                rounded={null}
                                checked={Boolean(state[checkbox])}
                                onChange={onChangeCheckbox(checkbox)}
                                label={t(`admin:${checkbox}`)}
                                data-testid={`checkbox-${textToId(checkbox, index)}`}
                                size="medium"
                            />
                        </p>
                    ))}
                    {state.numSubjects && (
                        <div className="risk-overview-conf__item risk-overview-conf__item--right type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type">
                                <FieldSelector
                                    customClassName="edit-ROC-subjectField"
                                    fieldSelect="subjectField"
                                    onChange={onChangeSubjectField}
                                    label={
                                        state.numSubjects
                                            ? `${t('admin:subjectCalculation')}*`
                                            : t('admin:subjectCalculation')
                                    }
                                    labelField="subjectCalculation"
                                    customId="edit-ROC-subjectField"
                                    value={state.subjectField}
                                    dataSource={DashboardType.SITE}
                                    formats={[]}
                                    tags={[FieldTag.SUBJECT_POPULATION]}
                                    error={subjectCalculationError}
                                />
                            </FormControl>
                        </div>
                    )}
                </div>
            </ItemWrapper>
        </div>
    );
};

export default EditRiskOverviewConfig;
